import { useEffect } from 'react';

interface MangaItem {
  title: string;
  description: string;
  thumbnail: string;
  episodes: number;
  latestEpisode: number;
  status: string;
  genre: string;
  rating: number;
  reads: string;
  tags: string[];
  updatedAt: string;
  author: string;
}

interface MangaDetailModalProps {
  manga: MangaItem | null;
  onClose: () => void;
}

const statusColor: Record<string, string> = {
  '連載中': 'bg-green-100 text-green-700 border-green-200',
  '完結': 'bg-gray-100 text-gray-600 border-gray-200',
  '休載中': 'bg-orange-100 text-orange-700 border-orange-200',
};

const mangaDetails: Record<string, {
  synopsis: string;
  highlights: string[];
  sampleEpisodes: { num: number; title: string; pages: number; isNew?: boolean }[];
  authorProfile: string;
}> = {
  '薬剤師まんが日記': {
    synopsis: '現役薬剤師のビースト先生が、薬局での日常をコミカルかつリアルに描いた4コマ漫画シリーズ。患者さんとのやりとり、スタッフ間のあるある、調剤ミスを防ぐ工夫など、薬局の「あるある」が満載。笑いながら学べる人気作品です。',
    highlights: ['薬局あるあるをユーモラスに描写', '服薬指導の実践的なヒントが随所に', '新人薬剤師の成長ストーリー', '患者さんとの心温まるエピソード'],
    sampleEpisodes: [
      { num: 50, title: '年末の薬局は戦場だ！', pages: 4, isNew: true },
      { num: 49, title: 'ジェネリックを勧めるタイミング', pages: 4, isNew: true },
      { num: 48, title: '新人薬剤師の初めての服薬指導', pages: 4 },
      { num: 47, title: '患者さんの「薬が余ってる」問題', pages: 4 },
      { num: 46, title: '在宅訪問で起きたハプニング', pages: 4 },
    ],
    authorProfile: '現役薬剤師として15年のキャリアを持つ漫画家。薬局の日常をリアルかつユーモラスに描くことで、薬剤師の仕事の魅力を広く伝えることを目指している。',
  },
  '薬の歴史物語': {
    synopsis: '古代エジプトから現代の最先端医薬品まで、薬の発見と発展の歴史をマンガで解説する全12話の完結作品。アスピリンの発見、ペニシリンの奇跡、抗がん剤の開発など、薬にまつわる感動的なエピソードを丁寧に描きます。',
    highlights: ['古代から現代までの薬の歴史を網羅', 'ノーベル賞受賞者の研究エピソード', '日本の薬学の発展史も収録', '薬の発見にまつわる感動的な実話'],
    sampleEpisodes: [
      { num: 12, title: '現代の奇跡〜mRNAワクチンの誕生', pages: 24 },
      { num: 11, title: '抗がん剤の革命〜分子標的治療薬', pages: 22 },
      { num: 10, title: '日本の薬学〜和漢薬から近代薬学へ', pages: 20 },
      { num: 9, title: 'ペニシリンの奇跡〜偶然が生んだ救世主', pages: 24 },
      { num: 8, title: 'アスピリン物語〜100年愛される薬', pages: 18 },
    ],
    authorProfile: '薬学博士の資格を持つ漫画家・医療ライター。複雑な薬学の知識をわかりやすいストーリーに落とし込む技術に定評がある。医療系雑誌での連載経験多数。',
  },
  '薬局あるある': {
    synopsis: '薬局で働くすべての人が「わかる！」と共感できるあるあるネタを集めたコメディマンガ。処方箋の読み解き、患者さんの不思議な質問、薬剤師同士の会話など、クスッと笑えるエピソードが30話収録されています。',
    highlights: ['薬剤師なら絶対共感できるネタ満載', '患者さんの視点からのあるあるも収録', '薬局スタッフ間のリアルな会話', '笑いながら薬局業務を振り返れる'],
    sampleEpisodes: [
      { num: 30, title: '「副作用が怖い」と言う患者さんへの対応', pages: 8, isNew: true },
      { num: 29, title: '処方箋の字が読めない！', pages: 8 },
      { num: 28, title: '「薬が多すぎる」と言われたら', pages: 8 },
      { num: 27, title: '在庫切れの薬を求める患者さん', pages: 8 },
      { num: 26, title: '薬剤師あるある〜研修会の洗礼', pages: 8 },
    ],
    authorProfile: '薬局勤務経験10年の元薬剤師が描く、リアルすぎる薬局コメディ。「笑いは最良の薬」をモットーに、薬局業界の日常を明るく発信している。',
  },
  '薬剤師キャリア図鑑': {
    synopsis: '調剤薬局・病院薬剤師・ドラッグストア・製薬会社・在宅医療など、薬剤師のさまざまなキャリアパスをマンガで紹介。それぞれの職場の魅力・やりがい・大変さをリアルに描き、キャリア選択の参考になる一冊。',
    highlights: ['10種類以上のキャリアパスを紹介', '各職場のリアルな一日を描写', '転職を考える薬剤師必見の内容', '薬学生のキャリア選択にも最適'],
    sampleEpisodes: [
      { num: 8, title: '在宅医療専門薬剤師の一日', pages: 16, isNew: true },
      { num: 7, title: '製薬会社MRから薬剤師へ転身', pages: 14 },
      { num: 6, title: '病院薬剤師〜ICUでの仕事', pages: 18 },
      { num: 5, title: 'ドラッグストア薬剤師の本音', pages: 12 },
      { num: 4, title: '調剤薬局の管理薬剤師になるまで', pages: 16 },
    ],
    authorProfile: '薬剤師キャリアコンサルタントとして活動する傍ら、マンガで薬剤師のキャリアを発信。自身も調剤薬局・病院・製薬会社と多様なキャリアを歩んできた。',
  },
  '薬と食べ物の不思議な関係': {
    synopsis: 'グレープフルーツと薬の相互作用、納豆とワーファリン、牛乳と抗生物質など、身近な食べ物と薬の意外な関係をマンガで解説。患者さんへの服薬指導にも役立つ知識が満載の実用的な作品。',
    highlights: ['グレープフルーツ問題を徹底解説', '納豆・ワーファリン相互作用の仕組み', '牛乳・アルコールと薬の関係', '日常食品との相互作用一覧付き'],
    sampleEpisodes: [
      { num: 20, title: 'コーヒーと薬〜カフェインの影響', pages: 10, isNew: true },
      { num: 19, title: 'サプリメントと薬の危険な組み合わせ', pages: 12 },
      { num: 18, title: 'アルコールと薬〜絶対NGな組み合わせ', pages: 10 },
      { num: 17, title: '牛乳で飲んではいけない薬', pages: 8 },
      { num: 16, title: 'グレープフルーツの罠〜CYP3A4の話', pages: 14 },
    ],
    authorProfile: '臨床薬剤師として20年のキャリアを持つ薬剤師漫画家。患者さんへの服薬指導で実際に使っているエピソードをもとに、わかりやすいマンガを制作している。',
  },
  '薬局経営マンガ塾': {
    synopsis: '独立開業を目指す薬剤師や、薬局経営に悩む管理薬剤師のための実践的なマンガ。経営の基礎知識、スタッフマネジメント、患者獲得戦略、DX推進など、薬局経営のリアルを描きます。',
    highlights: ['薬局開業の手順をステップで解説', 'スタッフマネジメントの実践例', '経営改善のための数字の見方', '地域密着型薬局の成功事例'],
    sampleEpisodes: [
      { num: 15, title: '薬局DX〜電子処方箋導入の現実', pages: 20, isNew: true },
      { num: 14, title: 'スタッフが辞めない職場づくり', pages: 18 },
      { num: 13, title: '患者数を増やす地域連携の方法', pages: 16 },
      { num: 12, title: '薬局の収支を改善する3つの視点', pages: 20 },
      { num: 11, title: '開業1年目の失敗と学び', pages: 22 },
    ],
    authorProfile: '薬局チェーンの経営コンサルタントとして活動する薬剤師。自身も独立開業の経験を持ち、薬局経営の現場で得た知識をマンガで発信している。',
  },
};

export default function MangaDetailModal({ manga, onClose }: MangaDetailModalProps) {
  useEffect(() => {
    if (manga) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [manga]);

  if (!manga) return null;

  const detail = mangaDetails[manga.title] ?? {
    synopsis: manga.description,
    highlights: [],
    sampleEpisodes: [],
    authorProfile: '',
  };

  const stars = Array.from({ length: 5 }, (_, i) => i < Math.round(manga.rating));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* オーバーレイ */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* モーダル本体 */}
      <div
        className="relative bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ヘッダー：サムネイル＋タイトル */}
        <div className="relative h-60 overflow-hidden rounded-t-2xl">
          <img
            src={manga.thumbnail}
            alt={manga.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />

          {/* 閉じるボタン */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>

          {/* ステータスバッジ */}
          <div className="absolute top-4 left-4 flex gap-2">
            <span className={`text-xs px-3 py-1 rounded-full font-medium border ${statusColor[manga.status] ?? 'bg-gray-100 text-gray-600'}`}>
              {manga.status}
            </span>
            <span className="text-xs px-3 py-1 rounded-full font-medium bg-amber-400 text-white">
              {manga.genre}
            </span>
          </div>

          {/* タイトル・メタ情報 */}
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <h2 className="text-2xl font-bold text-white mb-2 drop-shadow">{manga.title}</h2>
            <div className="flex flex-wrap items-center gap-4 text-sm text-white/90">
              <span className="flex items-center gap-1">
                <i className="ri-book-open-line"></i>
                全{manga.episodes}話
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {manga.reads}回読まれた
              </span>
              <span className="flex items-center gap-1">
                {stars.map((filled, i) => (
                  <i key={i} className={filled ? 'ri-star-fill text-amber-400' : 'ri-star-line text-white/60'}></i>
                ))}
                <span className="ml-1 font-medium">{manga.rating}</span>
              </span>
            </div>
          </div>
        </div>

        {/* コンテンツ本体 */}
        <div className="p-6 space-y-6">

          {/* タグ */}
          <div className="flex flex-wrap gap-2">
            {manga.tags.map((tag) => (
              <span key={tag} className="text-xs bg-amber-50 text-amber-700 px-3 py-1 rounded-full border border-amber-200 font-medium">
                #{tag}
              </span>
            ))}
          </div>

          {/* あらすじ */}
          <div>
            <h3 className="text-base font-bold text-gray-900 mb-2 flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-file-text-line text-amber-500"></i>
              </span>
              あらすじ
            </h3>
            <p className="text-sm text-gray-600 leading-relaxed">{detail.synopsis}</p>
          </div>

          {/* 見どころ */}
          {detail.highlights.length > 0 && (
            <div>
              <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-star-line text-amber-500"></i>
                </span>
                見どころ
              </h3>
              <ul className="space-y-2">
                {detail.highlights.map((point, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-amber-400"></i>
                    </span>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 作者プロフィール */}
          {detail.authorProfile && (
            <div className="bg-amber-50 rounded-xl p-4 flex items-start gap-3">
              <span className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5 bg-amber-200 rounded-full">
                <i className="ri-user-line text-amber-700"></i>
              </span>
              <div>
                <p className="text-xs font-semibold text-amber-700 mb-1">作者について</p>
                <p className="text-sm text-gray-700 leading-relaxed">{detail.authorProfile}</p>
                <p className="text-xs text-amber-600 mt-1 font-medium">著者：{manga.author}</p>
              </div>
            </div>
          )}

          {/* エピソード一覧 */}
          {detail.sampleEpisodes.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-list-check text-amber-500"></i>
                  </span>
                  エピソード一覧
                </h3>
                <span className="text-xs text-gray-400">最新{detail.sampleEpisodes.length}話を表示</span>
              </div>
              <div className="space-y-2">
                {detail.sampleEpisodes.map((ep) => (
                  <div
                    key={ep.num}
                    className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:border-amber-200 hover:bg-amber-50/50 transition-colors cursor-pointer group"
                  >
                    <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-amber-400 transition-colors">
                      <span className="text-xs font-bold text-amber-600 group-hover:text-white transition-colors">
                        {ep.num}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-gray-800 font-medium truncate">{ep.title}</p>
                        {ep.isNew && (
                          <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-medium flex-shrink-0">NEW</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400">{ep.pages}ページ</p>
                    </div>
                    <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-amber-400 transition-colors flex-shrink-0"></i>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 更新情報 */}
          <div className="flex items-center gap-2 text-xs text-gray-400 border-t pt-4">
            <i className="ri-time-line"></i>
            <span>最終更新：{manga.updatedAt}</span>
            <span>・</span>
            <span>第{manga.latestEpisode}話まで配信中</span>
          </div>

          {/* アクションボタン */}
          <div className="flex gap-3">
            <button className="flex-1 bg-amber-500 hover:bg-amber-600 text-white py-3 rounded-xl font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
              <i className="ri-book-open-line text-lg"></i>
              第1話から読む
            </button>
            <button className="flex-1 border border-amber-300 hover:bg-amber-50 text-amber-600 py-3 rounded-xl font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
              <i className="ri-skip-forward-line text-lg"></i>
              最新話を読む
            </button>
            <button className="w-12 h-12 border border-gray-200 hover:border-amber-300 hover:bg-amber-50 rounded-xl flex items-center justify-center transition-colors cursor-pointer flex-shrink-0">
              <i className="ri-bookmark-line text-gray-500 hover:text-amber-500"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
